import { chakra, ChakraProps } from "@chakra-ui/react";
import { BULL_SILHOUETTE_PATH } from "./bullSilhouettePath";

// Secondary accent used alongside the partner accent, matching bullprint's
// Solana violet -> green gradient on the bull.
const VIOLET = "#9945ff";

interface BullSilhouetteProps extends ChakraProps {
  /** Partner accent (hex) that the bull glows in. */
  accent: string;
}

// Charging-bull watermark reused from bullprint. The path is a stack of
// overlapping circles/arcs, so it only reads as a bull when filled (a solid
// nonzero fill fuses them into one silhouette) — stroking it alone just shows
// the construction geometry. We render three layers: a blurred bloom halo, the
// crisp gradient silhouette, and a whisper-faint white rim for a premium edge.
export const BullSilhouette = ({ accent, ...props }: BullSilhouetteProps) => (
  <chakra.svg
    viewBox="0 0 1000 620"
    role="img"
    aria-label="charging bull silhouette"
    {...props}
  >
    <defs>
      <linearGradient id="ansem-bull-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={VIOLET} />
        <stop offset="100%" stopColor={accent} />
      </linearGradient>
      <filter
        id="ansem-bull-bloom"
        x="-40%"
        y="-40%"
        width="180%"
        height="180%"
      >
        <feGaussianBlur stdDeviation="16" />
      </filter>
    </defs>
    {/* Soft bloom halo. */}
    <path
      d={BULL_SILHOUETTE_PATH}
      fill="url(#ansem-bull-grad)"
      filter="url(#ansem-bull-bloom)"
      opacity={0.4}
    />
    {/* Crisp gradient silhouette. */}
    <path d={BULL_SILHOUETTE_PATH} fill="url(#ansem-bull-grad)" opacity={0.9} />
    {/* Whisper-faint rim for a constructed, premium edge. */}
    <path
      d={BULL_SILHOUETTE_PATH}
      fill="none"
      stroke="#ffffff"
      strokeOpacity={0.08}
      strokeWidth={0.8}
    />
  </chakra.svg>
);
